import React, {PropsWithChildren, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TopSlidesAnalyticsEnum} from '../../../../incase-domain/proptypes/slidesAnalytics';
import {FormattedClicksAnalytics, SlideClicksAnalytics} from '../../../../incase-domain/analytics';
import {IPresentation} from '../../../../incase-domain/presentation';
import {slideIndex} from '../../../../utils/slide';
import {compareByValue} from '../../../util/util';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import Label from '../../../../incase-domain/components/label/Label';
import ReportProgressBar from '../../../../incase-domain/components/report/progressBar/ReportProgressBar';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';

interface SlideClicksProps {
    selectedPresentation: IPresentation;
    slideClicks: SlideClicksAnalytics[];
    isLoading: boolean;
}

function SlideClicksReport(props: PropsWithChildren<SlideClicksProps>): JSX.Element {
    const {selectedPresentation, slideClicks, isLoading} = props;
    const [formattedData, setFormattedData] = useState<FormattedClicksAnalytics>({sum: 0, data: []});

    const {t} = useTranslation();

    const [topSlidesAnalytics, setTopSlidesAnalytics] = useState<TopSlidesAnalyticsEnum>(TopSlidesAnalyticsEnum.ALL);

    useEffect(() => {
        setFormattedData({sum: 0, data: []});
        let sum = 0;
        slideClicks.forEach((slide, i) => {
            if (i < topSlidesAnalytics || topSlidesAnalytics === TopSlidesAnalyticsEnum.ALL) {
                let sClicks = 0;
                const slideData = slideIndex(selectedPresentation, slide.slideId);
                const cords = Object.values(slide.clicks);
                cords.forEach((cord) =>
                    Object.values(cord).forEach((value) => {
                        sum += value;
                        sClicks += value;
                    })
                );
                setFormattedData((prev) => ({
                    ...prev,
                    data: [
                        ...prev.data,
                        {
                            id: slide.slideId,
                            name: slideData.name,
                            value: sClicks,
                        },
                    ],
                }));
            }
        });
        setFormattedData((prev) => ({
            sum,
            data: prev.data.sort(compareByValue),
        }));
    }, [selectedPresentation, slideClicks, topSlidesAnalytics]);

    const maxClicks = useMemo(() => {
        return formattedData.data.reduce((max, slide) => Math.max(max, slide.value), 0);
    }, [formattedData]);

    const topSlidesDropdownItems = [
        [
            {
                label: t('reportPage.all'),
                onClick: () => setTopSlidesAnalytics(TopSlidesAnalyticsEnum.ALL),
            },
            {
                label: `${t('reportPage.topViewedSlides')}: 5`,
                onClick: () => setTopSlidesAnalytics(TopSlidesAnalyticsEnum.FIVE),
            },
            {
                label: `${t('reportPage.topViewedSlides')}: 10`,
                onClick: () => setTopSlidesAnalytics(TopSlidesAnalyticsEnum.TEN),
            },
        ],
    ];

    return (
        <SkeletonLoader isLoading={isLoading} props={{style: {minHeight: '12rem'}}}>
            <div className="u-flex u-flex-col u-gap-3">
                <div className="u-flex u-h-between u-v-center">
                    <Label size="lg">{t('reportPage.slideClicksPie.title')}</Label>
                    <DropdownMenuCompact menuPosition={DropdownMenuCompactMenuPosition.LEFT} items={topSlidesDropdownItems}>
                        <Button variant="ghost" size="sm" trailing="solar:alt-arrow-down-linear">{`${t('reportPage.topViewedSlides')}: ${
                            topSlidesAnalytics || t('reportPage.all')
                        }`}</Button>
                    </DropdownMenuCompact>
                </div>
                <div className="u-flex u-flex-col u-gap-2 u-ml-2">
                    {formattedData.data.length > 0 ? (
                        formattedData.data.map((slide) => (
                            <ReportProgressBar
                                label={slide.name}
                                value={slide.value}
                                maxValue={maxClicks}
                                score={t('reportPage.clicks', {count: slide.value})}
                                hideValue
                            />
                        ))
                    ) : (
                        <Label size="md">{t('reportPage.noData')}</Label>
                    )}
                </div>
            </div>
        </SkeletonLoader>
    );
}

export default SlideClicksReport;
